import { useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import { Building2, Heart } from "lucide-react";
import SpliceLogo from "@/components/icons/SpliceLogo";
import BusinessCard from "@/components/directory/BusinessCard";
import { BusinessListing } from "@/types/directory";
import {
  useSharedClientAccount,
  useSharedFavoriteBusinesses,
} from "@/lib/apollo/hooks/favorites";
import { buildFavoritesOpenGraph } from "@/lib/openGraph";
import { bookingSiteOrigin } from "@/lib/siteUrl";
import OpenGraphTags from "@/components/seo/OpenGraphTags";

interface GqlSalon {
  id: string;
  city: string | null;
  state: string | null;
}

interface GqlSpecialty {
  id: string;
  name: string;
}

interface GqlBusiness {
  id: string;
  name: string;
  slug: string | null;
  about: string | null;
  logoUrl: string | null;
  headerImageUrl: string | null;
  bookingUrlIdentifier: string | null;
  countryCode: string | null;
  timezone: string | null;
  acceptsOnlineBookings: boolean | null;
  businessSpecialties: GqlSpecialty[] | null;
  salons: GqlSalon[] | null;
}

const adaptBusiness = (b: GqlBusiness): BusinessListing => ({
  id: b.id,
  name: b.name,
  about: b.about,
  logo_url: b.logoUrl,
  header_image_url: b.headerImageUrl,
  booking_url_identifier: b.bookingUrlIdentifier ?? b.slug ?? b.id,
  timezone: b.timezone,
  specialties: (b.businessSpecialties ?? []).map((s) => s.name),
  locations: (b.salons ?? []).map((s) => ({ city: s.city, state: s.state })),
  salon_hours: [],
  closed_periods: [],
  review_summary: { count: 0, average: null },
});

const BusinessCardSkeleton = () => (
  <div className="bg-white rounded-lg shadow-sm overflow-hidden border border-grey-100 animate-pulse">
    <div className="h-32 bg-grey-100" />
    <div className="p-5">
      <div className="h-5 bg-grey-100 rounded w-3/4 mb-3" />
      <div className="h-4 bg-grey-100 rounded w-1/2 mb-3" />
      <div className="h-4 bg-grey-100 rounded w-full mb-2" />
      <div className="h-4 bg-grey-100 rounded w-2/3 mb-3" />
    </div>
  </div>
);

const formatJoinedDate = (iso: string | null | undefined) => {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString("en-US", {
      month: "long",
      year: "numeric",
    });
  } catch {
    return null;
  }
};

export default function SharedFavoritesPage() {
  const { shareToken } = useParams<{ shareToken: string }>();

  const { data: accountData, loading: accountLoading } =
    useSharedClientAccount(shareToken);
  const { data: businessesData, loading: businessesLoading } =
    useSharedFavoriteBusinesses(shareToken);

  const sharer = accountData?.sharedClientAccount;
  const edges = businessesData?.sharedFavoriteBusinesses?.edges ?? [];
  const businesses: BusinessListing[] = useMemo(
    () =>
      edges
        .map((e: { node: GqlBusiness | null }) => e.node)
        .filter((n: GqlBusiness | null): n is GqlBusiness => Boolean(n))
        .map(adaptBusiness),
    [edges]
  );

  const loading = accountLoading || businessesLoading;
  const joinedLabel = formatJoinedDate(sharer?.dateJoined);

  // Invalid / expired token: account query returned but null
  const tokenInvalid = !accountLoading && !sharer;

  const origin = bookingSiteOrigin();
  const pathname =
    typeof window !== "undefined" ? window.location.pathname : "/favorites";
  const openGraph = buildFavoritesOpenGraph({
    origin,
    pathname,
    sharerName: sharer?.fullName,
  });

  return (
    <>
      <OpenGraphTags {...openGraph} />

      <div className="min-h-screen bg-grey-50">
        <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-sm border-b border-grey-100">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center h-16 gap-4">
              <Link to="/" className="flex-shrink-0">
                <SpliceLogo className="h-6 w-auto" />
              </Link>
            </div>
          </div>
        </header>

        <section className="bg-gradient-to-br from-green-900 to-green-300 text-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
            <div className="text-center max-w-2xl mx-auto">
              <div className="inline-flex items-center justify-center h-12 w-12 bg-white/15 rounded-full mb-4">
                <Heart className="h-6 w-6 text-white" />
              </div>
              {accountLoading ? (
                <>
                  <div className="h-8 w-64 bg-white/20 rounded mx-auto mb-3 animate-pulse" />
                  <div className="h-5 w-40 bg-white/15 rounded mx-auto animate-pulse" />
                </>
              ) : sharer ? (
                <>
                  <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-3">
                    {sharer.fullName}'s favorites
                  </h1>
                  <p className="text-base sm:text-lg text-green-50/90">
                    {joinedLabel
                      ? `On Splice since ${joinedLabel}`
                      : "Curated picks on Splice"}
                  </p>
                </>
              ) : (
                <>
                  <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-3">
                    List unavailable
                  </h1>
                  <p className="text-base sm:text-lg text-green-50/90">
                    This shared link is invalid or has expired.
                  </p>
                </>
              )}
            </div>
          </div>
        </section>

        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-10">
          {tokenInvalid ? (
            <div className="flex flex-col items-center justify-center py-16 px-4">
              <div className="h-16 w-16 bg-grey-100 rounded-full flex items-center justify-center mb-4">
                <Building2 className="h-8 w-8 text-grey-400" />
              </div>
              <h3 className="text-lg font-semibold text-grey-900 mb-2">
                Nothing to see here
              </h3>
              <p className="text-grey-500 text-center max-w-md">
                The link you followed isn't valid. Ask the sender for a new
                share link, or browse businesses on Splice.
              </p>
              <Link
                to="/"
                className="mt-4 px-4 py-2 bg-green-300 hover:bg-green-400 text-white text-sm font-medium rounded-full transition-colors"
              >
                Explore businesses
              </Link>
            </div>
          ) : loading && businesses.length === 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {Array.from({ length: 8 }).map((_, index) => (
                <BusinessCardSkeleton key={index} />
              ))}
            </div>
          ) : businesses.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 px-4">
              <div className="h-16 w-16 bg-grey-100 rounded-full flex items-center justify-center mb-4">
                <Heart className="h-8 w-8 text-grey-400" />
              </div>
              <h3 className="text-lg font-semibold text-grey-900 mb-2">
                No favorites yet
              </h3>
              <p className="text-grey-500 text-center max-w-md">
                {sharer?.firstName ?? "This user"} hasn't favorited any
                businesses yet. Check back soon!
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {businesses.map((business) => (
                <BusinessCard key={business.id} business={business} />
              ))}
            </div>
          )}
        </main>

        <footer className="bg-white border-t border-grey-100 mt-8">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
            <div className="text-center text-grey-400 text-sm">
              <p>Powered by Splice</p>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
}
